import { useTranslation } from "react-i18next";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import Reveal from "../components/motion/Reveal";
import { useNewsItem } from "../hooks/useNews";
import { formatNewsDate, getNewsVisual } from "../components/news/newsVisuals";

export default function NewsDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { item, loading, error, notFound } = useNewsItem(id);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
        <Skeleton width={140} height={36} sx={{ mb: 3, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={200} sx={{ mb: 3, borderRadius: 4 }} />
        <Skeleton height={48} sx={{ mb: 2 }} />
        <Skeleton height={24} />
        <Skeleton height={24} />
        <Skeleton width="80%" height={24} />
      </Container>
    );
  }

  if (notFound) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 }, textAlign: "center" }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          {t("newsNotFound")}
        </Typography>
        <Button component={RouterLink} to="/news" startIcon={<ArrowBackRoundedIcon />}>
          {t("newsBack")}
        </Button>
      </Container>
    );
  }

  const visual = getNewsVisual(item);
  const date = formatNewsDate(item.newsDate, i18n.language);

  return (
    <Box>
      <Box
        sx={{
          position: "relative",
          height: { xs: 220, md: 280 },
          background: visual.gradient,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.25,
            backgroundImage:
              "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.6) 0, transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.4) 0, transparent 35%)",
          }}
        />
        <Container maxWidth="md" sx={{ position: "relative", height: "100%", display: "flex", alignItems: "flex-end", pb: 4 }}>
          <Stack spacing={1.5}>
            {item.appKey && (
              <Chip
                label={item.appKey}
                size="small"
                sx={{
                  alignSelf: "flex-start",
                  fontWeight: 700,
                  color: "#fff",
                  bgcolor: "rgba(7,11,24,0.35)",
                  backdropFilter: "blur(6px)",
                  border: "1px solid rgba(255,255,255,0.25)",
                }}
              />
            )}
            <Typography variant="h3" sx={{ color: "#fff", fontSize: { xs: "1.75rem", md: "2.5rem" }, lineHeight: 1.2 }}>
              {item.title}
            </Typography>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        {error && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 3 }}>
            {error}
          </Alert>
        )}

        <Button
          component={RouterLink}
          to="/news"
          startIcon={<ArrowBackRoundedIcon />}
          sx={{ mb: 3 }}
        >
          {t("newsBack")}
        </Button>

        <Reveal>
          {date && (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 3 }}>
              <CalendarTodayRoundedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              <Typography color="text.secondary">{date}</Typography>
            </Stack>
          )}
          <Typography
            color="text.secondary"
            sx={{ fontSize: { xs: "1.05rem", md: "1.15rem" }, lineHeight: 1.8, whiteSpace: "pre-line" }}
          >
            {item.body}
          </Typography>
        </Reveal>
      </Container>
    </Box>
  );
}
